import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field, Sel, inputCls } from "../components/Form";
import { PlanView } from "../components/space/LessonTools";
import { API, errMsg, useApi, loadFailed } from "../lib/api";
import { useMe } from "../lib/me";
import { useAuth } from "@clerk/react";

const EMPTY = { title: "", topic: "", level: "Beginner", goals: "", warmup: "", main_activity: "", practice: "", homework: "", room_id: "", course_id: "" };
const SECTIONS = [["goals", "Goals"], ["warmup", "Warm-up"], ["main_activity", "Main activity"], ["practice", "Practice"], ["homework", "Homework"]];
const KEYS = { goals: "goals", "warm-up": "warmup", "main activity": "main_activity", practice: "practice", homework: "homework" };

const parseDraft = (text) => {
  const out = {};
  const t = text.match(/TITLE:\s*(.+)/i);
  if (t) out.title = t[1].trim();
  text.split(/^##\s+/m).slice(1).forEach((chunk) => {
    const [head, ...body] = chunk.split("\n");
    const key = KEYS[head.trim().toLowerCase()];
    if (key) out[key] = body.join("\n").trim();
  });
  return out;
};

const AiDraft = ({ onUse }) => {
  const { getToken } = useAuth();
  const [d, setD] = useState({ topic: "", level: "Beginner", duration: 45, instrument: "" });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (d.topic.trim().length < 2) return toast.error("Type a topic first");
    setBusy(true);
    setText("");
    try {
      const res = await fetch(`${API}/lesson-plans/draft`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` }, body: JSON.stringify(d) });
      if (!res.ok) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", all = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          const ev = JSON.parse(line);
          if (ev.error) throw new Error(ev.error);
          all += ev.t;
          setText(all);
        }
      }
    } catch (e) { toast.error(e.message || "The AI draft failed. Please try again."); }
    setBusy(false);
  };
  return (
    <section className="panel p-5 space-y-3 border-yellow-400/30" data-testid="ai-draft-panel">
      <p className="flex items-center gap-2 font-bold"><Sparkles className="h-4 w-4 text-yellow-400" /> Draft with AI</p>
      <input className={inputCls} placeholder="Topic, e.g. Barre chords in F major" value={d.topic} onChange={(e) => setD({ ...d, topic: e.target.value })} data-testid="ai-topic-input" />
      <div className="grid grid-cols-3 gap-2">
        <Sel value={d.level} onChange={(v) => setD({ ...d, level: v })} testId="ai-level-select" options={["Beginner", "Intermediate", "Advanced"].map((l) => ({ value: l, label: l }))} />
        <input type="number" min={10} max={180} className={inputCls} value={d.duration} onChange={(e) => setD({ ...d, duration: +e.target.value || 45 })} data-testid="ai-duration-input" />
        <input className={inputCls} placeholder="Instrument" value={d.instrument} onChange={(e) => setD({ ...d, instrument: e.target.value })} data-testid="ai-instrument-input" />
      </div>
      <button onClick={run} disabled={busy} data-testid="ai-draft-btn" className="w-full flex items-center justify-center gap-2 rounded-[3px] bg-yellow-400 py-2 font-bold text-black hover:bg-yellow-300 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Writing…" : "Draft a plan"}
      </button>
      {text && (
        <>
          <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-[3px] bg-black p-3 text-xs text-zinc-300 font-sans" data-testid="ai-draft-output">{text}</pre>
          {!busy && <button onClick={() => onUse({ ...parseDraft(text), topic: d.topic, level: d.level })} data-testid="ai-use-draft-btn" className="w-full rounded-[3px] border border-yellow-400 py-2 text-sm font-semibold text-yellow-300 hover:bg-yellow-400/10">Use this draft</button>}
        </>
      )}
    </section>
  );
};

export default function Plans() {
  const api = useApi();
  const { isStaff } = useMe();
  const [plans, setPlans] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [rooms, setRooms] = useState([]);
  const [courses, setCourses] = useState([]);
  const load = useCallback(() => api.get("/lesson-plans").then((r) => setPlans(r.data)).catch(loadFailed("lesson plans")), [api]);
  useEffect(() => {
    load();
    api.get("/lobby").then((r) => { setRooms(r.data.rooms); setCourses(r.data.courses); }).catch(loadFailed("rooms and courses"));
  }, [api, load]);
  const pick = (p) => { setSel(p.id); setF({ ...EMPTY, ...p, room_id: p.room_id || "", course_id: p.course_id || "" }); };
  const save = async () => {
    if (!f.title) return toast.error("Give the plan a title");
    const body = { ...f, room_id: f.room_id || null, course_id: f.course_id || null };
    try {
      const { data } = sel ? await api.put(`/lesson-plans/${sel}`, body) : await api.post("/lesson-plans", body);
      toast.success("Plan saved");
      setSel(data.id);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => { try { await api.delete(`/lesson-plans/${sel}`); setSel(null); setF(EMPTY); load(); } catch (e) { toast.error(errMsg(e)); } };
  const current = plans.find((p) => p.id === sel);

  return (
    <div className="spotlight min-h-screen" data-testid="plans-page">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10">
        <span className="mono-label text-yellow-300">Lesson plans</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">{isStaff ? "Plan your next lesson" : "Your course lesson plans"}</h1>
        <div className={`mt-8 grid gap-6 ${isStaff ? "lg:grid-cols-[260px_1fr_360px]" : "lg:grid-cols-[260px_1fr]"}`}>
          <aside className="panel p-3 h-fit space-y-1" data-testid="plans-list">
            {isStaff && <button onClick={() => { setSel(null); setF(EMPTY); }} data-testid="new-plan-btn" className="w-full flex items-center gap-2 rounded-[3px] px-3 py-2 text-sm text-yellow-300 hover:bg-yellow-400/10"><Plus className="h-4 w-4" /> New plan</button>}
            {plans.length === 0 && <p className="px-3 py-2 text-sm text-zinc-500">No plans yet.</p>}
            {plans.map((p) => (
              <button key={p.id} onClick={() => pick(p)} data-testid={`plan-item-${p.id}`} className={`w-full text-left rounded-[3px] px-3 py-2 text-sm truncate transition-colors ${sel === p.id ? "bg-yellow-400/10 text-yellow-300" : "hover:bg-white/5"}`}>{p.title}</button>
            ))}
          </aside>
          {isStaff ? (
            <section className="panel p-5 space-y-4" data-testid="plan-editor">
              <div className="grid sm:grid-cols-[1fr_160px] gap-3">
                <Field label="Title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="plan-title-input" /></Field>
                <Field label="Level"><Sel value={f.level} onChange={(v) => setF({ ...f, level: v })} testId="plan-level-select" options={["Beginner", "Intermediate", "Advanced"].map((l) => ({ value: l, label: l }))} /></Field>
              </div>
              {SECTIONS.map(([k, label]) => (
                <Field key={k} label={label}><textarea rows={k === "main_activity" ? 6 : 3} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`plan-${k}-input`} /></Field>
              ))}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Attach to room"><Sel value={f.room_id} onChange={(v) => setF({ ...f, room_id: v })} testId="plan-room-select" placeholder="None" options={rooms.map((r) => ({ value: r.id, label: r.name }))} /></Field>
                <Field label="Attach to course"><Sel value={f.course_id} onChange={(v) => setF({ ...f, course_id: v })} testId="plan-course-select" placeholder="None" options={courses.map((c) => ({ value: c.id, label: c.title }))} /></Field>
              </div>
              <div className="flex gap-2">
                <button onClick={save} data-testid="plan-save-btn" className="flex-1 rounded-[3px] bg-yellow-400 py-2.5 font-bold text-black hover:bg-yellow-300">Save plan</button>
                {sel && <button onClick={remove} data-testid="plan-delete-btn" className="rounded-[3px] border border-zinc-700 px-3 text-zinc-400 hover:text-pink-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </section>
          ) : (
            <section className="panel p-5">{current ? <PlanView plan={current} /> : <p className="text-sm text-zinc-500">Pick a plan to read it. Plans from courses you've joined show up here.</p>}</section>
          )}
          {isStaff && <AiDraft onUse={(d) => setF((x) => ({ ...x, ...d }))} />}
        </div>
      </div>
    </div>
  );
}
